// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file normalizeMessage.js
 * @description Normalizador central de mensajes. Toda entrada al store (API, WS, REST, optimista)
 * debe pasar por normalizeMessage para garantizar formato consistente.
 * Garantiza SIEMPRE: id, conversationId, userId, text. `createdAt`/`fecha_envio` vacíos si el backend no envía fecha (orden por id).
 * El campo `text` sale SOLO de `raw.text` (sin fallback a mensaje/contenido).
 */

/**
 * ID de fila `conversation_subjects` en mensaje normalizado; `null` = General histórico (sin columna o NULL en BD).
 * @param {object|null|undefined} msgLike
 * @returns {number|null}
 */
export function normalizeMessageSubjectRowId(msgLike) {
  if (msgLike == null || typeof msgLike !== 'object') return null;
  const v = msgLike.subject_id ?? msgLike.raw?.subject_id;
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Alinea status/delivery_status con `estado_lectura` (español, join estados_mensajes en API).
 * Evita regresión: COALESCE(m.delivery_status,'sent') deja "sent" aunque el par ya haya leído.
 * @param {object} raw
 * @param {object} built
 * @returns {Record<string, string>}
 */
function mergeEstadoLecturaIntoDeliveryStatus(raw, built) {
  const el = String(raw?.estado_lectura ?? built?.estado_lectura ?? '')
    .toLowerCase()
    .trim();
  if (el === 'leido' || el === 'read' || el === 'seen' || el === 'visto') {
    return { status: 'read', delivery_status: 'read' };
  }
  if (el === 'entregado' || el === 'delivered') {
    return { status: 'delivered', delivery_status: 'delivered' };
  }
  if (el === 'enviado' || el === 'sent' || el === 'sending' || el === 'pending') {
    return {};
  }
  return {};
}

/**
 * Garantiza string visible; objetos → '' + aviso (evita romper React).
 * @param {*} val
 * @param {string} fieldName
 * @param {string|number} messageId
 * @returns {string}
 */
function coerceVisibleString(val, fieldName, messageId) {
  if (val == null || val === '') return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof console !== 'undefined') {
    console.warn('[NORMALIZE_MESSAGE_NON_STRING_FIELD]', { id: messageId, field: fieldName, type: typeof val });
  }
  return '';
}

/**
 * Normaliza un mensaje crudo (API, WebSocket, REST) al formato del store.
 * Si cid/uid inválidos → retorna null y log [PIPE][DROP] (no añadir al store).
 * @param {Object} raw - Mensaje crudo (puede tener mensaje, contenido, body, id, mensaje_id, etc.)
 * @returns {Object|null} { id, conversationId, userId, text, createdAt } + legacy, o null si inválido
 */
export function normalizeMessage(raw) {
  if (raw == null || typeof raw !== 'object') {
    console.error('[PIPE][DROP] raw null or not object', raw);
    return null;
  }

  const isTraceMsg =
    (typeof raw?.mensaje === 'string' && raw.mensaje.includes('PRO_V2_REALTIME_ROOTCAUSE_001')) ||
    (typeof raw?.text === 'string' && raw.text.includes('PRO_V2_REALTIME_ROOTCAUSE_001'));
  if (isTraceMsg) {
    console.log('[DEBUG_TRACE][FE_NORMALIZE_MSG_INPUT]', JSON.stringify({
      id: raw?.id ?? raw?.mensaje_id,
      display_text: String(raw?.display_text ?? '').slice(0, 200),
      text: String(raw?.text ?? '').slice(0, 200),
      mensaje: String(raw?.mensaje ?? '').slice(0, 200),
      contenido_original: String(raw?.contenido_original ?? '').slice(0, 200),
      original_text: String(raw?.original_text ?? raw?.mensaje_original ?? '').slice(0, 200),
      traducciones: raw?.traducciones,
      translations_map: raw?.translations_map,
      source_language: raw?.source_language ?? raw?.idioma_original,
      target_language: raw?.target_language ?? raw?.idioma_traducido,
    }));
  }

  const id = raw.id ?? raw.mensaje_id ?? 0;
  const numId = typeof id === 'string' && String(id).startsWith('temp-') ? id : Number(id);
  const conversationId = Number(raw.conversacion_id ?? raw.conversation_id ?? 0);
  const userId = Number(raw.remitente_id ?? raw.usuario_id ?? raw.emisor_id ?? raw.emisor ?? 0);

  if (!Number.isInteger(conversationId) || conversationId <= 0) {
    console.error('[PIPE][DROP] cid inválido', { raw, cid: conversationId });
    return null;
  }
  if (!Number.isInteger(userId) || userId <= 0) {
    console.error('[PIPE][DROP] uid inválido', { raw, uid: userId });
    return null;
  }

  const rawDeliveryView =
    raw.delivery_view && typeof raw.delivery_view === 'object' && !Array.isArray(raw.delivery_view)
      ? raw.delivery_view
      : raw.deliveryView && typeof raw.deliveryView === 'object' && !Array.isArray(raw.deliveryView)
        ? raw.deliveryView
        : null;
  const deliveredDisplayText = rawDeliveryView?.display_text
    ? coerceVisibleString(rawDeliveryView.display_text, 'delivered_display_text', id)
    : '';
  const displayFromPayload = coerceVisibleString(raw.display_text, 'display_text', id);
  const translatedStr = coerceVisibleString(
    raw.translated_text ?? raw.translatedText,
    'translated_text',
    id
  );
  const textRaw = coerceVisibleString(raw.text, 'text', id);
  const originalStr = coerceVisibleString(
    raw.original_text ?? raw.contenido_original ?? raw.mensaje_original,
    'original_text',
    id
  );

  const pickNonEmpty = (...parts) => {
    for (const p of parts) {
      const s = typeof p === 'string' ? p.trim() : String(p || '').trim();
      if (s !== '') return s;
    }
    return '';
  };

  // Contrato: frozen delivery_view > original. Traducción solo como metadata separada.
  const markerRaw = JSON.stringify(raw);
  if (markerRaw.includes('REVERSE_EN_TO_ES') || markerRaw.includes('FORWARD_ES_TO_EN')) {
    console.log('[AUTH_DEBUG][normalizeMessage]', JSON.stringify({
      id, remitente_id: raw.remitente_id,
      input_delivery_view: raw.delivery_view,
      input_delivered_display_text: raw.delivered_display_text,
      rawDeliveryView,
      deliveredDisplayText,
      displayFromPayload,
      mensajeRaw: (raw.mensaje || '').slice(0, 200),
    }));
  }
  const text = pickNonEmpty(
    deliveredDisplayText,
    originalStr,
    raw.mensaje != null ? coerceVisibleString(raw.mensaje, 'mensaje', id) : '',
    raw.contenido != null ? coerceVisibleString(raw.contenido, 'contenido', id) : '',
    displayFromPayload,
    translatedStr,
    textRaw,
    raw.message != null ? coerceVisibleString(raw.message, 'message', id) : '',
    raw.body != null ? coerceVisibleString(raw.body, 'body', id) : ''
  );
  /** Orden/visor: no dejar que created_at pise fecha_envio si esta existe (incluso formato MySQL). */
  let fechaEnvioRaw = null;
  if (raw.fecha_envio != null && String(raw.fecha_envio).trim() !== '') {
    fechaEnvioRaw = raw.fecha_envio;
  } else if (raw.fecha_envio_iso != null && String(raw.fecha_envio_iso).trim() !== '') {
    fechaEnvioRaw = String(raw.fecha_envio_iso).trim();
  } else if (raw.created_at != null && String(raw.created_at).trim() !== '') {
    fechaEnvioRaw = raw.created_at;
  } else if (raw.fecha != null && String(raw.fecha).trim() !== '') {
    fechaEnvioRaw = raw.fecha;
  }
  let createdAt = null;
  if (fechaEnvioRaw != null && fechaEnvioRaw !== '') {
    if (typeof fechaEnvioRaw === 'string') {
      const t = fechaEnvioRaw.trim();
      createdAt = t !== '' ? t : null;
    } else if (typeof fechaEnvioRaw.toISOString === 'function') {
      createdAt = fechaEnvioRaw.toISOString();
    }
  }
  const translatedTextRaw =
    raw.translated_text ??
    raw.translatedText ??
    raw.traduccion_guardada ??
    raw.traduccion_para_mostrar ??
    null;
  const translatedCoerced =
    translatedTextRaw != null ? coerceVisibleString(translatedTextRaw, 'translatedText', id).trim() : '';
  const translatedText = translatedCoerced !== '' ? translatedCoerced : null;

  const fechaEnvioStr = createdAt ?? '';

  const rawSubjectId = raw.subject_id ?? raw.subject_row_id;
  let subject_id = null;
  if (rawSubjectId != null && rawSubjectId !== '') {
    const sn = Number(rawSubjectId);
    if (Number.isFinite(sn) && sn > 0) subject_id = sn;
  }

  const outId = (numId > 0 && !Number.isNaN(numId) && numId !== Infinity) ? numId : (typeof (raw.id ?? id) === 'string' ? (raw.id ?? id) : 0);
  const normalizedMessage = {
    id: outId,
    mensaje_id: raw.mensaje_id ?? outId,
    conversationId: conversationId,
    conversacion_id: conversationId,
    userId: userId,
    remitente_id: userId,
    usuario_id: userId,
    text: text || '',
    mensaje: text || '',
    contenido: text || '',
    contenido_original: coerceVisibleString(
      raw.contenido_original ?? raw.mensaje_original,
      'contenido_original',
      id
    ),
    subject_id,
    raw,
    createdAt: fechaEnvioStr,
    fecha_envio: fechaEnvioStr,
    created_at: fechaEnvioStr,
    translatedText: translatedText || undefined,
    delivered_display_text: deliveredDisplayText || undefined,
    _delivery_lang: rawDeliveryView?.display_lang || undefined,
    _translation_failed: rawDeliveryView?.translation_failed === true || rawDeliveryView?.translation_failed === 1 || rawDeliveryView?.translation_failed === '1' ? true : undefined,
    display_text: pickNonEmpty(deliveredDisplayText, displayFromPayload, text || '', originalStr),
    ...(raw.source_language != null && String(raw.source_language).trim() !== ''
      ? { source_language: String(raw.source_language).trim() }
      : raw.idioma_original != null && String(raw.idioma_original).trim() !== ''
        ? { source_language: String(raw.idioma_original).trim() }
        : {}),
    ...(raw.target_language != null && String(raw.target_language).trim() !== ''
      ? { target_language: String(raw.target_language).trim() }
      : raw.idioma_traducido != null && String(raw.idioma_traducido).trim() !== ''
        ? { target_language: String(raw.idioma_traducido).trim() }
        : {}),
    ...(originalStr !== '' ? { original_text: originalStr } : {}),
    ...(translatedText != null && String(translatedText).trim() !== ''
      ? { translated_text: String(translatedText) }
      : {}),
    ...(raw.language != null && String(raw.language).trim() !== ''
      ? { language: String(raw.language) }
      : raw.idioma != null && String(raw.idioma).trim() !== ''
        ? { language: String(raw.idioma) }
        : {}),
    ...(raw.traducciones && { traducciones: raw.traducciones }),
    ...(raw.translations_map != null &&
    typeof raw.translations_map === 'object' &&
    !Array.isArray(raw.translations_map)
      ? { translations_map: raw.translations_map }
      : {}),
    ...(raw.idioma_original && { idioma_original: raw.idioma_original }),
    ...(raw.idioma_traducido && { idioma_traducido: raw.idioma_traducido }),
    ...(raw.tipo && { tipo: raw.tipo }),
    ...(raw.estado_lectura && { estado_lectura: raw.estado_lectura }),
    // `status` primero; `delivery_status` después para que read/delivered no queden pisados por status legacy.
    ...(raw.status && { status: raw.status }),
    ...(raw.delivery_status && { delivery_status: raw.delivery_status, status: raw.delivery_status }),
    ...(raw.__translated !== undefined && { __translated: raw.__translated }),
    ...(raw.__entradaAnim === true ? { __entradaAnim: true } : {}),
    ...(raw.archivo_data !== undefined && { archivo_data: raw.archivo_data }),
    ...(raw.archivo_url !== undefined && { archivo_url: raw.archivo_url }),
    ...(raw.remitente_nombre && { remitente_nombre: raw.remitente_nombre }),
    ...(raw.remitente_avatar && { remitente_avatar: raw.remitente_avatar }),
    ...(raw.fecha_envio_iso != null &&
    String(raw.fecha_envio_iso).trim() !== '' && { fecha_envio_iso: String(raw.fecha_envio_iso).trim() }),
    ...(raw.is_deleted === true || raw.is_deleted === 1 || raw.is_deleted === '1'
      ? { is_deleted: true }
      : {}),
    ...(raw.deleted_at != null && String(raw.deleted_at).trim() !== ''
      && String(raw.deleted_at) !== '0000-00-00 00:00:00'
      ? { deleted_at: raw.deleted_at, is_deleted: true }
      : {}),
    ...(raw.deleted_by != null && raw.deleted_by !== '' ? { deleted_by: raw.deleted_by } : {}),
    ...(raw.deleted_for_everyone === 1 || raw.deleted_for_everyone === true || raw.deleted_for_everyone === '1'
      ? { deleted_for_everyone: true, is_deleted: true }
      : {}),
    ...(raw.deleted_for_me !== undefined ? { deleted_for_me: raw.deleted_for_me } : {}),
    ...(raw.originalTextSnapshot !== undefined && raw.originalTextSnapshot !== null
      ? { originalTextSnapshot: String(raw.originalTextSnapshot) }
      : {}),
    ...(raw.translation_failed === true || raw.translation_failed === 1 || raw.translation_failed === '1'
      ? { translation_failed: true }
      : {}),
    ...(raw.translation_source != null && String(raw.translation_source).trim() !== ''
      ? { translation_source: String(raw.translation_source).trim() }
      : {}),
  };
  if (isTraceMsg) {
    console.log('[DEBUG_TRACE][FE_NORMALIZE_MSG_OUTPUT]', JSON.stringify({
      id: normalizedMessage.id,
      text: String(normalizedMessage.text ?? '').slice(0, 200),
      mensaje: String(normalizedMessage.mensaje ?? '').slice(0, 200),
      display_text: String(normalizedMessage.display_text ?? '').slice(0, 200),
      contenido_original: String(normalizedMessage.contenido_original ?? '').slice(0, 200),
      subject_id: normalizedMessage.subject_id,
      traducciones: normalizedMessage.traducciones,
      translations_map: normalizedMessage.translations_map,
      source_language: normalizedMessage.source_language,
      target_language: normalizedMessage.target_language,
      translation_failed: normalizedMessage.translation_failed,
    }));
  }

  if (typeof console !== 'undefined' && import.meta.env.DEV) {
    try {
      console.log('[FRONT_TRANSLATION_RENDER_INPUT]', {
        id: normalizedMessage.id,
        source_language: normalizedMessage.source_language,
        target_language: normalizedMessage.target_language,
        display_text_type: typeof normalizedMessage.display_text,
        translated_text_type: typeof normalizedMessage.translated_text,
        text_type: typeof normalizedMessage.text,
      });
    } catch (_) {
      // noop
    }
  }
  try {
    if (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('ARCHAT_DEBUG_LANGUAGE_RUNTIME') === '1'
    ) {
      console.info('[LANG_NORMALIZE_CHOICE]', {
        id: normalizedMessage.id,
        final_text: String(normalizedMessage.text ?? '').slice(0, 120),
        display_text_raw: String(displayFromPayload ?? '').slice(0, 80),
        translated_text_raw: String(translatedStr ?? '').slice(0, 80),
        text_raw: String(textRaw ?? '').slice(0, 80),
        original_text_raw: String(originalStr ?? '').slice(0, 80),
        source_language: normalizedMessage.source_language ?? null,
        target_language: normalizedMessage.target_language ?? null,
        translation_failed: normalizedMessage.translation_failed ?? false,
        ts: Date.now(),
      });
    }
  } catch (_) { /* noop */ }
  const outputText = normalizedMessage.text || '';
  if (outputText.includes('REVERSE_EN_TO_ES') || outputText.includes('FORWARD_ES_TO_EN')) {
    console.log('[AUTH_DEBUG][normalizeMessage_OUTPUT]', JSON.stringify({
      id: normalizedMessage.id,
      remitente_id: normalizedMessage.remitente_id,
      output_text: (normalizedMessage.text || '').slice(0, 200),
      output_display_text: (normalizedMessage.display_text || '').slice(0, 200),
      output_mensaje: (normalizedMessage.mensaje || '').slice(0, 200),
      delivered_display_text: normalizedMessage.delivered_display_text?.slice(0, 200),
      raw_delivery_view: raw.delivery_view,
      raw_delivery_view_dt: raw.delivery_view?.display_text?.slice(0, 200),
      hasRawDv: !!(raw.delivery_view && typeof raw.delivery_view === 'object'),
    }));
  }
  return {
    ...normalizedMessage,
    ...mergeEstadoLecturaIntoDeliveryStatus(raw, normalizedMessage),
  };
}
